import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, MapPin, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo, navLinks } from '../../data/mock';

const dienstenItems = [
  { name: 'Alle diensten', path: '/diensten' },
  { name: 'Fitnessapparatuur verhuizen', path: '/diensten/fitnessapparatuur-verhuizen' },
  { name: 'Voor & Na projecten', path: '/voor-en-na' },
];

const desktopLinks = [
  { name: 'Home', path: '/' },
  { name: 'Blog', path: '/blog' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Over ons', path: '/over-ons' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dienstenOpen, setDienstenOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  const openDiensten = () => { clearTimeout(closeTimer.current); setDienstenOpen(true); };
  const closeDiensten = () => { closeTimer.current = setTimeout(() => setDienstenOpen(false), 150); };
  const dienstenActive = location.pathname.startsWith('/diensten') || location.pathname === '/voor-en-na';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg shadow-neutral-900/5' : 'bg-white'
    }`}>
      {/* Top bar */}
      <div className="bg-black text-white py-2.5">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-yellow-400 transition-colors group">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                <Phone className="w-3.5 h-3.5" />
              </div>
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="hidden sm:flex items-center gap-2 hover:text-yellow-400 transition-colors group">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                <Mail className="w-3.5 h-3.5" />
              </div>
              <span className="font-medium">{companyInfo.email}</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-2 text-neutral-400">
            <MapPin className="w-3.5 h-3.5" />
            <span>{companyInfo.location}</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-4 py-0">
        <div className="flex items-center justify-between">
          {/* NEXON logo */}
          <Link to="/" aria-label="NEXON Installatie & Service - Home" className="flex items-center py-2" data-testid="header-logo">
            <img
              src="/nexon-logo-white.png"
              alt="NEXON Installatie & Service"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`relative px-4 py-2 text-sm font-semibold transition-colors rounded-lg ${
                location.pathname === '/' ? 'text-yellow-400' : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
              }`}
            >
              Home
            </Link>

            {/* Diensten dropdown */}
            <div className="relative" onMouseEnter={openDiensten} onMouseLeave={closeDiensten}>
              <button
                className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold transition-colors rounded-lg ${
                  dienstenActive ? 'text-yellow-400' : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
                onClick={() => setDienstenOpen((v) => !v)}
                data-testid="nav-diensten-dropdown"
              >
                Diensten <ChevronDown className={`w-4 h-4 transition-transform ${dienstenOpen ? 'rotate-180' : ''}`} />
              </button>
              {dienstenOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-2xl shadow-xl border border-neutral-100 py-2 z-50" data-testid="diensten-dropdown-menu">
                  {dienstenItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setDienstenOpen(false)}
                      className={`block px-5 py-2.5 text-sm font-semibold transition-colors ${
                        location.pathname === item.path ? 'text-yellow-500 bg-yellow-50' : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {desktopLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-semibold transition-colors rounded-lg ${
                  location.pathname === link.path 
                    ? 'text-yellow-400' 
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-yellow-400 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${companyInfo.phone}`}>
              <Button variant="ghost" className="text-neutral-600 hover:text-yellow-400 hover:bg-yellow-50 font-semibold">
                <Phone className="w-4 h-4 mr-2" />
                Bel direct
              </Button>
            </a>
            <Link to="/offerte">
              <Button className="bg-yellow-400 hover:bg-yellow-300 text-black px-6 rounded-xl shadow-lg shadow-yellow-400/25 hover:shadow-yellow-400/40 transition-all duration-300 font-bold tracking-wide">
                Offerte aanvragen
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2.5 text-neutral-700 hover:bg-neutral-100 rounded-xl transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 py-6 border-t border-neutral-100 animate-slide-up">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-semibold rounded-xl transition-colors ${
                    location.pathname === link.path 
                      ? 'text-yellow-400 bg-yellow-50' 
                      : 'text-neutral-600 hover:bg-neutral-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-neutral-100">
                <a href={`tel:${companyInfo.phone}`}>
                  <Button variant="outline" className="w-full justify-center border-neutral-200 font-semibold">
                    <Phone className="w-4 h-4 mr-2" />
                    Bel direct
                  </Button>
                </a>
                <Link to="/offerte" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold">
                    Offerte aanvragen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
